import { getGenericFallbackMessage, getParseFallbackMessage, getStatusMessage } from '@/lib/api/error-messages';
import { safeErrorCode } from '@/utils/safeErrorMetadata';
import type { BloominPaymentResult } from './BloominCardInputForm';

/** Keep provider response bodies out of error/telemetry objects. */
export class BloominInitiateError extends Error {
  constructor(message: string, readonly status: number, readonly code?: string) {
    super(message);
    this.name = 'BloominInitiateError';
  }
}

export async function readBloominInitiateResponse(response: Response): Promise<BloominPaymentResult> {
  let body: unknown;
  try {
    body = await response.json();
  } catch {
    throw new BloominInitiateError(
      getStatusMessage(response.status) ?? getParseFallbackMessage(),
      response.status,
      'INVALID_RESPONSE',
    );
  }
  const envelope = typeof body === 'object' && body !== null
    ? body as Record<string, unknown>
    : {};
  const nested = typeof envelope.error === 'object' && envelope.error !== null
    ? envelope.error as Record<string, unknown>
    : {};
  if (!response.ok || envelope.success !== true || (typeof envelope.data !== 'object' || envelope.data === null)) {
    const message = [envelope.message, nested.message, envelope.error]
      .find((value): value is string => typeof value === 'string' && value.trim().length > 0);
    const rawCode = envelope.code ?? envelope.error_code ?? nested.code;
    const code = safeErrorCode(rawCode);
    throw new BloominInitiateError(
      getStatusMessage(response.status) ?? message ?? getGenericFallbackMessage(),
      response.status,
      code,
    );
  }
  return envelope.data as BloominPaymentResult;
}
